const createSection = (id, label, title, description, items = []) => ({
  id,
  label,
  title,
  description,
  items,
});

export const LINC_FORGE_STAGES = Object.freeze([
  "Idea",
  "Visualize",
  "Refine",
  "3D",
  "Prototype",
  "Shop Prep",
  "Production",
  "Launch",
  "Orders",
]);

const stageDetails = {
  Idea: ["Product brief", "Audience and use", "Reference capture"],
  Visualize: ["Generate concept", "Upload references", "Select direction"],
  Refine: ["Compare revisions", "Annotate changes", "Confirm design"],
  "3D": ["Model views", "Dimensions", "Printable geometry"],
  Prototype: ["Test print", "Fit and function", "Capture feedback"],
  "Shop Prep": ["Materials", "Production recipe", "Cost and capacity"],
  Production: ["Work order", "Batch progress", "Quality checks"],
  Launch: ["Product story", "Listing assets", "Price and channels"],
  Orders: ["Demand", "Fulfillment", "Customer status"],
};

function workflowWorkspace(stage, index) {
  return {
    id: `lincforge-${stage.toLowerCase().replaceAll(" ", "-")}`,
    eyebrow: `Stage ${index + 1} of ${LINC_FORGE_STAGES.length}`,
    title: stage,
    summary: `Move the active product through ${stage.toLowerCase()} with one clear next action.`,
    accent: ["cyan", "blue", "violet", "teal", "amber", "orange", "red", "pink", "green"][index],
    access: "manager",
    object: {
      name: "Lacrosse Player + Pen",
      kind: "Paired product concept",
      mediaLabel: "Product image / GLB",
      owner: index < 6 ? "Design team" : "Operations",
      status: index === 0 ? "Active" : index === 1 ? "Ready next" : "Queued",
      revision: "R-03",
      nextAction: stageDetails[stage][0],
      approvals: index < 2 ? "None due" : "1 checkpoint",
    },
    sections: [
      createSection(
        "work",
        "Work",
        `${stage} workspace`,
        "The tactical work stays inside this card; the complete dashboard does not become a long page.",
        stageDetails[stage],
      ),
      createSection(
        "history",
        "History",
        "Decisions and revisions",
        "Review the evidence that produced the current direction.",
        ["Latest decision", "Revision notes", "Linked references"],
      ),
      createSection(
        "people",
        "People",
        "Owners and collaborators",
        "Department-head and partner identities are resolved through the avatar registry.",
        ["Stage owner", "Assigned collaborators", "Pending reviewer"],
      ),
    ],
  };
}

export const lincForgeWorkflowConfig = {
  id: "lincforge-product-workflow",
  title: "ForgeLinc",
  subtitle: "Product workflow",
  currentRole: "manager",
  summary: [
    { id: "workload", label: "Active products", value: "2", tone: "neutral" },
    { id: "blockers", label: "Blockers", value: "1", tone: "danger" },
    { id: "approvals", label: "Approvals", value: "2", tone: "warning" },
    { id: "deadlines", label: "Due soon", value: "3", tone: "neutral" },
    { id: "progress", label: "Workflow", value: "38%", progress: 38, tone: "success" },
  ],
  workspaces: LINC_FORGE_STAGES.map(workflowWorkspace),
  addWorkspace: {
    id: "add-workspace",
    kind: "add",
    title: "Add workspace",
    summary: "Create a custom category, stage, tool, or view.",
    access: "manage",
  },
};

export const founderOverviewConfig = {
  id: "founder-overview",
  title: "Founder Overview",
  subtitle: "Operating view",
  currentRole: "manager",
  summary: [
    { id: "workload", label: "Active work", value: "24", tone: "neutral" },
    { id: "blockers", label: "Blockers", value: "3", tone: "danger" },
    { id: "approvals", label: "Approvals", value: "5", tone: "warning" },
    { id: "deadlines", label: "Due soon", value: "7", tone: "neutral" },
    { id: "progress", label: "On track", value: "78%", progress: 78, tone: "success" },
  ],
  workspaces: [
    {
      id: "founder-lincforge",
      eyebrow: "Create and produce",
      title: "ForgeLinc",
      summary: "Products, design, prototypes, production, launch, and orders.",
      accent: "cyan",
      access: "manager",
      object: {
        name: "Money Slide",
        kind: "Current priority product",
        mediaLabel: "Current product",
        owner: "Founder",
        status: "Launch preparation",
        revision: "Production ready",
        nextAction: "Create marketplace media",
        approvals: "1 due",
      },
      sections: [
        createSection("now", "Now", "Founder attention", "The smallest useful view of what needs action now.", [
          "Approve launch image direction",
          "Review Lacrosse Player + Pen concept",
          "Resolve one production blocker",
        ]),
        createSection("products", "Products", "Product pipeline", "Open an object-focused workflow without losing founder context.", [
          "Money Slide",
          "Lacrosse Player + Pen",
        ]),
        createSection("decisions", "Decisions", "Pending decisions", "Decision records include owner, evidence, and downstream impact.", [
          "Launch channel sequence",
          "Prototype color split",
        ]),
      ],
    },
    {
      id: "founder-focus",
      eyebrow: "Attention and decisions",
      title: "FocusLinc",
      summary: "Founder priorities, conversations, decisions, and next actions.",
      accent: "blue",
      access: "manager",
      object: {
        name: "Today’s operating plan",
        kind: "Founder focus",
        mediaLabel: "Plan snapshot",
        owner: "Founder",
        status: "In progress",
        revision: "Today",
        nextAction: "Clear highest blocker",
        approvals: "3 due",
      },
      sections: [
        createSection("attention", "Attention", "What needs you", "Prioritized by urgency, dependency, and founder leverage.", [
          "Critical decision",
          "Blocked workstream",
          "Delegated follow-up",
        ]),
        createSection("history", "History", "Recent context", "Conversation, decisions, and completed actions remain connected.", [
          "Latest briefing",
          "Decisions made",
          "Work completed",
        ]),
      ],
    },
    {
      id: "founder-build",
      eyebrow: "Software delivery",
      title: "BuildLinc",
      summary: "Products, repositories, builds, validation, and deployment readiness.",
      accent: "violet",
      access: "manager",
      object: {
        name: "CommandLinc",
        kind: "Software product",
        mediaLabel: "Build preview",
        owner: "Build lead",
        status: "Local validation",
        revision: "Integration build",
        nextAction: "Validate interface shell",
        approvals: "None due",
      },
      sections: [
        createSection("delivery", "Delivery", "Build status", "A compact view of the current build and its evidence.", [
          "Current checkpoint",
          "Validation results",
          "Release readiness",
        ]),
        createSection("risks", "Risks", "Delivery risks", "Literal statements identify the problem, impact, and required action.", [
          "Open defect",
          "Missing dependency",
          "Next validation",
        ]),
      ],
    },
    {
      id: "founder-partners",
      eyebrow: "Scoped collaboration",
      title: "Partner Hub",
      summary: "Role-based partner workspaces across permitted products and modules.",
      accent: "teal",
      access: "manager",
      object: {
        name: "Partner portfolio",
        kind: "External collaboration",
        mediaLabel: "Partner overview",
        owner: "Partner lead",
        status: "2 active",
        revision: "Access policy V1",
        nextAction: "Review module permissions",
        approvals: "2 access requests",
      },
      sections: [
        createSection("partners", "Partners", "Active partners", "People are records inside partner organizations, not hard-coded features.", [
          "Assigned organizations",
          "Open deliverables",
          "Recent contributions",
        ]),
        createSection("access", "Access", "Role and scope", "Control view, contribute, edit, approve, and manage per module and object.", [
          "Role templates",
          "Object scopes",
          "Audit trail",
        ]),
      ],
    },
  ],
  addWorkspace: {
    id: "add-workspace",
    kind: "add",
    title: "Add workspace",
    summary: "Create a custom founder category, module view, or operating panel.",
    access: "manage",
  },
};

export const avatarRegistry = {
  linc: {
    id: "linc",
    label: "Linc",
    role: "Oversight",
    scope: "system",
    assetKey: "canonical-linc-glb",
  },
  arti: {
    id: "arti",
    label: "Arti",
    role: "Creative + Build",
    scope: "creative-build",
    assetKey: "canonical-arti-glb",
  },
  departmentHeads: [],
};
